import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Globe, 
  BarChart3, 
  DollarSign, 
  Eye, 
  MousePointer, 
  CheckCircle,
  XCircle,
  Ban,
  Trash2,
  RefreshCw,
  ExternalLink,
  TrendingUp,
  Activity
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';

interface WordPressSite {
  _id: string;
  affiliateId: string;
  siteName: string;
  wordpressUrl: string;
  adminEmail: string;
  status: 'active' | 'suspended';
  totalEarnings: number;
  totalPaid: number;
  createdAt: string;
  stats: {
    totalClicks: number;
    todayClicks: number;
    totalEarnings: number;
    pendingEarnings: number;
    approvedEarnings: number;
    paidEarnings: number;
    totalConversions: number;
    conversionRate: string;
  };
}

interface SitesResponse {
  success: boolean;
  data: {
    sites: WordPressSite[];
    pagination: {
      total: number;
      page: number;
      pages: number;
      limit: number;
    };
  };
}

const fetchSites = async (params: any): Promise<SitesResponse> => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${API_URL}/admin/affiliate/wordpress-sites`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return data;
};

export function AdminWordPressSites() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: 'all',
    page: 1,
    limit: 20,
    search: ''
  });

  const { data: sitesData, isLoading, error, refetch } = useQuery({
    queryKey: ['wordpress-sites', filters],
    queryFn: () => fetchSites(filters),
    staleTime: 30000,
    retry: false,
    refetchOnWindowFocus: false
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ siteId, status }: { siteId: string; status: string }) => {
      const token = localStorage.getItem('token');
      return axios.put(
        `${API_URL}/admin/affiliate/wordpress-sites/${siteId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      toast.success('Site status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['wordpress-sites'] });
    },
    onError: (error: any) => {
      console.error('Status update error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update site status';
      toast.error(errorMessage);
    }
  });

  const deleteSiteMutation = useMutation({
    mutationFn: async (siteId: string) => {
      const token = localStorage.getItem('token');
      return axios.delete(
        `${API_URL}/admin/affiliate/wordpress-sites/${siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      toast.success('Site deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['wordpress-sites'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete site');
    }
  });

  const handleSuspend = (siteId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    if (confirm(`Are you sure you want to ${newStatus === 'suspended' ? 'suspend' : 'activate'} this site?`)) {
      updateStatusMutation.mutate({ siteId, status: newStatus });
    }
  };

  const handleDelete = (siteId: string) => {
    if (confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      deleteSiteMutation.mutate(siteId);
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return <Badge className="bg-green-500 text-white"><CheckCircle className="size-3 mr-1" /> Active</Badge>;
    }
    return <Badge className="bg-red-500 text-white"><Ban className="size-3 mr-1" /> Suspended</Badge>;
  };

  // Calculate totals
  const totals = sitesData?.data.sites.reduce(
    (acc, site) => ({
      totalEarnings: acc.totalEarnings + site.stats.totalEarnings,
      pendingEarnings: acc.pendingEarnings + site.stats.pendingEarnings,
      approvedEarnings: acc.approvedEarnings + site.stats.approvedEarnings,
      paidEarnings: acc.paidEarnings + site.stats.paidEarnings,
      totalClicks: acc.totalClicks + site.stats.totalClicks,
      totalConversions: acc.totalConversions + site.stats.totalConversions
    }),
    { totalEarnings: 0, pendingEarnings: 0, approvedEarnings: 0, paidEarnings: 0, totalClicks: 0, totalConversions: 0 }
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-gray-900">WordPress Plugin Sites</h1>
          <p className="text-gray-600 mt-1">Manage websites using your affiliate WordPress plugin</p>
        </div>
        <Button onClick={() => refetch()} variant="outline">
          <RefreshCw className="size-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Card className="p-8 bg-red-50 border-red-200">
          <div className="flex items-center justify-center flex-col">
            <XCircle className="size-12 text-red-500 mb-4" />
            <h3 className="text-xl text-red-900 mb-2">Backend Connection Error</h3>
            <p className="text-red-700 text-center mb-4">
              Unable to connect to the backend server. Please ensure your backend is running.
            </p>
            <div className="bg-red-100 p-4 rounded-lg mb-4 text-sm text-red-800">
              <p className="mb-2">Current API URL: <code className="bg-red-200 px-2 py-1 rounded">{API_URL}</code></p>
              <p>To fix this:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Set <code className="bg-red-200 px-1 rounded">VITE_BACKEND_URL</code> environment variable</li>
                <li>Or set backend URL in localStorage: <code className="bg-red-200 px-1 rounded">localStorage.setItem('BACKEND_URL', 'your-url')</code></li>
                <li>Make sure your Node.js backend server is running</li>
              </ul>
            </div>
            <Button onClick={() => refetch()} variant="outline">
              <RefreshCw className="size-4 mr-2" />
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Summary Cards */}
      {!error && totals && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Total Sites</p>
                <p className="text-2xl text-blue-900 mt-1">
                  {sitesData?.data.pagination.total || 0}
                </p>
              </div>
              <Globe className="size-10 text-blue-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Total Earnings</p>
                <p className="text-2xl text-green-900 mt-1">
                  ₹{totals.totalEarnings.toLocaleString()}
                </p>
              </div>
              <DollarSign className="size-10 text-green-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Total Clicks</p>
                <p className="text-2xl text-purple-900 mt-1">
                  {totals.totalClicks.toLocaleString()}
                </p>
              </div>
              <MousePointer className="size-10 text-purple-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700">Conversions</p>
                <p className="text-2xl text-orange-900 mt-1">
                  {totals.totalConversions}
                </p>
              </div>
              <TrendingUp className="size-10 text-orange-600 opacity-50" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm text-gray-600 mb-2 block">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-600 mb-2 block">Search</label>
            <Input
              placeholder="Search by site name, URL or email"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="text-sm text-gray-600 mb-2 block">Per Page</label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value), page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Sites Grid */}
      <div className="grid grid-cols-1 gap-6">
        {isLoading ? (
          <Card className="p-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="size-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading sites...</span>
            </div>
          </Card>
        ) : sitesData && sitesData.data.sites.length > 0 ? (
          sitesData.data.sites.map((site) => (
            <Card key={site._id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Globe className="size-6 text-rose-600" />
                    <div>
                      <h3 className="text-xl text-gray-900">{site.siteName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <a 
                          href={site.wordpressUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          {site.wordpressUrl}
                          <ExternalLink className="size-3" />
                        </a>
                        {getStatusBadge(site.status)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Affiliate ID</p>
                      <p className="text-sm text-gray-900 mt-1">{site.affiliateId}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Admin Email</p>
                      <p className="text-sm text-gray-900 mt-1">{site.adminEmail}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Registered</p>
                      <p className="text-sm text-gray-900 mt-1">
                        {new Date(site.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600">Conversion Rate</p>
                      <p className="text-sm text-gray-900 mt-1">{site.stats.conversionRate}%</p>
                    </div>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-1">
                        <MousePointer className="size-4 text-blue-600" />
                        <p className="text-xs text-blue-700">Total Clicks</p>
                      </div>
                      <p className="text-lg text-blue-900">{site.stats.totalClicks}</p>
                      <p className="text-xs text-blue-600 mt-1">Today: {site.stats.todayClicks}</p>
                    </div>

                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="size-4 text-green-600" />
                        <p className="text-xs text-green-700">Total Earnings</p>
                      </div>
                      <p className="text-lg text-green-900">₹{site.stats.totalEarnings.toLocaleString()}</p>
                      <p className="text-xs text-green-600 mt-1">{site.stats.totalConversions} conversions</p>
                    </div>

                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Activity className="size-4 text-yellow-600" />
                        <p className="text-xs text-yellow-700">Pending</p>
                      </div>
                      <p className="text-lg text-yellow-900">₹{site.stats.pendingEarnings.toLocaleString()}</p>
                    </div>

                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="size-4 text-purple-600" />
                        <p className="text-xs text-purple-700">Approved</p>
                      </div>
                      <p className="text-lg text-purple-900">₹{site.stats.approvedEarnings.toLocaleString()}</p>
                    </div>

                    <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-200">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="size-4 text-indigo-600" />
                        <p className="text-xs text-indigo-700">Paid</p>
                      </div>
                      <p className="text-lg text-indigo-900">₹{site.stats.paidEarnings.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 ml-4">
                  <Button
                    size="sm"
                    variant={site.status === 'active' ? 'destructive' : 'default'}
                    onClick={() => handleSuspend(site._id, site.status)}
                    className="whitespace-nowrap"
                  >
                    {site.status === 'active' ? (
                      <>
                        <Ban className="size-4 mr-1" />
                        Suspend
                      </>
                    ) : (
                      <>
                        <CheckCircle className="size-4 mr-1" />
                        Activate
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(site._id)}
                  >
                    <Trash2 className="size-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-8 text-center text-gray-500">
            <Globe className="size-12 mx-auto text-gray-300 mb-4" />
            <p>No WordPress sites registered yet</p>
            <p className="text-sm mt-2">Sites will appear here once they install and activate the WordPress plugin</p>
          </Card>
        )}
      </div>

      {/* Pagination */}
      {sitesData && sitesData.data.pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {((filters.page - 1) * filters.limit) + 1} to{' '}
            {Math.min(filters.page * filters.limit, sitesData.data.pagination.total)} of{' '}
            {sitesData.data.pagination.total} sites
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
              variant="outline"
            >
              Previous
            </Button>
            <Button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page >= sitesData.data.pagination.pages}
              variant="outline"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}